"use client";

import React from "react";
import { Building2, Dog, ChevronRight, ShieldCheck, Sparkles, Activity } from "lucide-react";
import { SynaptixLogo } from "@repo/ui/logo";

export default function WelcomePage() {
  return (
    <main suppressHydrationWarning className="min-h-screen bg-[#060B14] text-white flex flex-col justify-between py-8 px-4 md:px-12 relative overflow-hidden font-sans">
      {/* Dynamic Background Glow Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[700px] h-[700px] rounded-full bg-blue-600/10 blur-[160px] pointer-events-none" />

      {/* Top Header & Branding */}
      <header className="text-center z-10 max-w-4xl mx-auto mt-4 mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6 backdrop-blur-md">
          <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-300">
            Plataforma Médica Veterinaria Inteligente
          </span>
        </div>

        <div className="flex items-center justify-center mb-4">
          <SynaptixLogo size={55} showText={true} />
        </div>

        <p className="text-base md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
          Bienvenido a la red de diagnóstico ecográfico veterinario con respaldo de Inteligencia Artificial. Por favor selecciona tu portal de acceso:
        </p>
      </header>

      {/* Main Selector Cards Container */}
      <section className="w-full max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 my-auto z-10 py-6">
        
        {/* TARJETA 1: CLÍNICAS VETERINARIAS (B2B) */}
        <div className="group relative bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 hover:border-cyan-500/50 rounded-3xl p-8 flex flex-col justify-between transition-all duration-500 hover:shadow-[0_0_40px_rgba(6,182,212,0.15)] hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="w-16 h-16 rounded-2xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400 group-hover:scale-110 group-hover:bg-cyan-500/25 transition-all duration-300 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
                <Building2 className="w-9 h-9" />
              </div>
              <span suppressHydrationWarning className="text-xs font-bold uppercase tracking-widest text-cyan-400 bg-cyan-500/10 px-3 py-1.5 rounded-full border border-cyan-500/20">
                Portal Clínicas
              </span>
            </div>

            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 group-hover:text-cyan-300 transition-colors">
              Portal para Clínicas y Hospitales Veterinarios
            </h2>
            <p className="text-sm md:text-base text-slate-400 mb-6 leading-relaxed">
              Plataforma profesional para médicos y centros veterinarios. Agenda ecografías especializadas en tu consultorio, gestiona tu crédito rotativo y descarga reportes diagnósticos al instante.
            </p>
          </div>

          <a
            href="https://clinicas.synaptixvet.com"
            className="w-full py-4 px-6 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold rounded-2xl flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(6,182,212,0.3)] transition-all duration-300 group-hover:shadow-[0_0_35px_rgba(6,182,212,0.5)]"
          >
            <span>Ingresar al Portal de Clínicas</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* TARJETA 2: TUTORES DE MASCOTAS (B2C) */}
        <div className="group relative bg-slate-900/60 backdrop-blur-xl border border-slate-800/80 hover:border-emerald-500/50 rounded-3xl p-8 flex flex-col justify-between transition-all duration-500 hover:shadow-[0_0_40px_rgba(16,185,129,0.15)] hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 group-hover:scale-110 group-hover:bg-emerald-500/25 transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                <Dog className="w-9 h-9" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-3 py-1.5 rounded-full border border-emerald-500/20">
                Atención Domiciliaria
              </span>
            </div>

            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 group-hover:text-emerald-300 transition-colors">
              Agendamiento de Ecografía a Domicilio
            </h2>
            <p className="text-sm md:text-base text-slate-400 mb-6 leading-relaxed">
              Solicita atención ecográfica especializada para tu perro o gato directamente en la comodidad de tu hogar. Carga tu remisión médica y programa la visita de nuestro equipo de especialistas.
            </p>
          </div>

          <a
            href="https://mascotas.synaptixvet.com"
            className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold rounded-2xl flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(16,185,129,0.3)] transition-all duration-300 group-hover:shadow-[0_0_35px_rgba(16,185,129,0.5)]"
          >
            <span>Pedir Cita para mi Mascota</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

      </section>

      {/* Footer Benefits / Trust Badges */}
      <footer className="z-10 text-center py-4 border-t border-slate-800/60 max-w-5xl mx-auto w-full flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span>Equipos Ecográficos Médicos de Alta Gama</span>
        </div>
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          <span>Auditoría Médica y Validación por Inteligencia Artificial</span>
        </div>
        <p className="text-slate-500">© 2026 SYNAPTIX VET. Todos los derechos reservados.</p>
      </footer>
    </main>
  );
}
